import { useState, useCallback } from "react";
import {
  DropZone,
  Button,
  BlockStack,
  InlineStack,
  Text,
  Card,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { upload } from "services/upload-file.service";
import { FileEntity } from "types/Files";

interface CustomFieldFileUploadProps {
  label: string;
  value: string | string[];
  onChange: (fileIds: string[]) => void;
  isRequired?: boolean;
  error?: string;
  existingFiles?: FileEntity[];
}

const CustomFieldFileUpload: React.FC<CustomFieldFileUploadProps> = ({
  label,
  value,
  onChange,
  isRequired = false,
  error,
  existingFiles = [],
}) => {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Convert value to array of file IDs, filtering out undefined/null values
  const fileIds = Array.isArray(value)
    ? value.filter((id) => id !== undefined && id !== null)
    : value
    ? [value]
    : [];

  // Helper function to get current file IDs from value prop (avoids stale closures)
  const getCurrentFileIds = useCallback((): string[] => {
    const currentValue = value;
    return Array.isArray(currentValue)
      ? currentValue.filter((id) => id !== undefined && id !== null)
      : currentValue
      ? [currentValue]
      : [];
  }, [value]);

  // Filter existing files to only include those still in fileIds
  const validExistingFiles = existingFiles.filter((file) =>
    fileIds.includes(file.id)
  );

  // Combine existing files with uploaded files
  const allFiles = [
    ...validExistingFiles.map((file) => ({
      id: file.id,
      name: file.title || file.name || `File ${file.id}`,
      isExisting: true,
    })),
    ...fileIds
      .filter((id) => !validExistingFiles.some((f) => f.id === id))
      .map((id) => ({
        id,
        name: `File ${id}`,
        isExisting: false,
      })),
  ];

  const handleDropZoneDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!companyId) return;

      setUploading(true);
      const newFileIds: string[] = [];
      const errors: Error[] = [];

      // Upload files one by one, tracking successes and failures
      for (const file of acceptedFiles) {
        try {
          const { data } = await upload(file, companyId);
          newFileIds.push(data);
        } catch (error) {
          errors.push(error as Error);
        }
      }

      // Save successfully uploaded files even if some failed
      // Read current fileIds from value prop to avoid stale closure
      if (newFileIds.length > 0) {
        const currentFileIds = getCurrentFileIds();
        const updatedFileIds = [...currentFileIds, ...newFileIds];
        onChange(updatedFileIds);
      }

      // Show error message if some files failed
      if (errors.length > 0) {
        // You could add a toast/notification here to inform the user
      }

      setUploading(false);
      // Note: onFileDialogClose will handle closing the dialog via toggleOpenFileDialog
    },
    [companyId, onChange, getCurrentFileIds]
  );

  const handleRemoveFile = useCallback(
    (fileIdToRemove: string) => {
      // Read current fileIds from value prop to avoid stale closure
      const currentFileIds = getCurrentFileIds();
      const updatedFileIds = currentFileIds.filter((id) => id !== fileIdToRemove);
      onChange(updatedFileIds);
    },
    [onChange, getCurrentFileIds]
  );

  const handleReplaceFile = useCallback(
    async (fileIdToReplace: string, newFile: File) => {
      if (!companyId) return;

      setUploading(true);
      try {
        const { data } = await upload(newFile, companyId);
        // Read current fileIds from value prop to avoid stale closure
        const currentFileIds = getCurrentFileIds();
        const updatedFileIds = currentFileIds.map((id) =>
          id === fileIdToReplace ? data : id
        );
        onChange(updatedFileIds);
      } catch (error) {
        console.error("Error replacing file:", error);
      } finally {
        setUploading(false);
        // Note: onFileDialogClose will handle closing the dialog via toggleOpenFileDialog
      }
    },
    [companyId, onChange, getCurrentFileIds]
  );

  const toggleOpenFileDialog = useCallback(() => {
    setOpenFileDialog((prev) => !prev);
  }, []);

  const handleReplaceClick = useCallback(
    (fileId: string) => {
      const input = document.createElement("input");
      input.type = "file";
      input.onchange = async (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (target.files && target.files[0]) {
          await handleReplaceFile(fileId, target.files[0]);
        }
      };
      input.click();
    },
    [handleReplaceFile]
  );

  return (
    <BlockStack gap="200">
      <Text as="p" variant="bodyMd" fontWeight="semibold">
        {label}
        {isRequired && <span style={{ color: "red" }}> *</span>}
      </Text>
      <DropZone
        allowMultiple={true}
        openFileDialog={openFileDialog}
        onDropAccepted={handleDropZoneDrop}
        onFileDialogClose={toggleOpenFileDialog}
        disabled={uploading}
      >
        <DropZone.FileUpload />
      </DropZone>
      <Button onClick={toggleOpenFileDialog} loading={uploading}>
        {t("upload_files") || "Upload Files"}
      </Button>

      {allFiles.length > 0 && (
        <BlockStack gap="200">
          <Text as="p" variant="bodyMd">
            {t("uploaded_files") || "Uploaded Files"}:
          </Text>
          {allFiles.map((file) => (
            <Card key={file.id}>
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Text as="p" variant="bodyMd">
                    📎 {file.name}
                  </Text>
                </InlineStack>
                <InlineStack gap="200">
                  {!file.isExisting && (
                    <Button
                      onClick={() => handleReplaceClick(file.id)}
                      size="slim"
                      variant="secondary"
                      disabled={uploading}
                    >
                      {t("replace")}
                    </Button>
                  )}
                  <Button
                    onClick={() => handleRemoveFile(file.id)}
                    size="slim"
                    tone="critical"
                    disabled={uploading}
                  >
                    {t("remove")}
                  </Button>
                </InlineStack>
              </InlineStack>
            </Card>
          ))}
        </BlockStack>
      )}

      {error && (
        <Text as="p" tone="critical" variant="bodySm">
          {error}
        </Text>
      )}
    </BlockStack>
  );
};

export default CustomFieldFileUpload;

